/**
 * Snake game configuration.
 * Grid dimensions are computed at runtime to fill the screen.
 */

export const SNAKE_CONFIG = {
    /** Target cell size — actual size adjusts to fill the screen evenly */
    TARGET_CELL_SIZE: 50,

    /**
     * Padding from screen edges in pixels.
     * Superseded by {@link SNAKE_UI} (top/bottom/side reserves) which is what
     * {@link computeGrid} actually consumes. Kept here only as a historical
     * reference for the old contract — `SNAKE_UI` is the live source.
     */

    /** Ticks per second at level 1 (snake moves once per tick) */
    BASE_TICK_RATE: 6,

    /** Tick rate increase per 5 food eaten */
    TICK_RATE_STEP: 0.8,

    /** Max tick rate cap */
    MAX_TICK_RATE: 16,

    /**
     * Multiplier applied to BASE / MAX tick rate AND TICK_RATE_STEP when
     * the `touchMode` setting is on. Lower = slower snake = friendlier
     * to thumb-driven input on a phone/tablet. 0.55 cuts snake speed
     * almost in half while still leaving room to ramp up with combos.
     */
    TOUCH_MODE_TICK_MULTIPLIER: 0.55,

    /** Starting snake length (must be even — each tram car is 2 cells) */
    INITIAL_LENGTH: 4,

    /** Cells added per food pickup (one tram car = 2 cells) */
    GROW_AMOUNT: 2,

    /** Points per food */
    POINTS_PER_FOOD: 10,

    /** Station mode: points earned per passenger picked up */
    STATION_POINTS_PER_PASSENGER: 10,
    /** Station mode: cells grown per passenger (2 cells = 1 tram car) */
    STATION_GROW_PER_PASSENGER: 2,

    /** Bonus points awarded on board transition */
    BOARD_CLEAR_BONUS: 50,

    /** Colors */
    COLOR_HEAD: 0x16a34a,
    COLOR_BODY: 0x22c55e,
    COLOR_FOOD: 0xfbbf24,
    COLOR_GRID_BG: 0x1e293b,
    COLOR_GRID_LINE: 0x334155,
    COLOR_WALL: 0x475569,

    // ── Obstacles ───────────────────────────────────────
    OBSTACLE_COUNT_MIN: 8,
    OBSTACLE_COUNT_MAX: 14,
    OBSTACLE_EXTRA_PER_BOARD: 2,
    OBSTACLE_CAP: 35,
    /**
     * Spawn templates. Each entry is one possible obstacle variant the
     * random spawner may pick. `slot` is forwarded to the theme manager
     * so the matching PNG is rendered; `type` stays coarse so collision
     * and fallback drawing keep working unchanged.
     *
     * Footprints follow the artwork dimensions (cell size = 50 px):
     *   2×2 → 100×100 px      2×3 → 100×150 px      3×2 → 150×100 px
     *   2×4 → 100×200 px      3×3 → 150×150 px      4×2 → 200×100 px
     *   4×4 → 200×200 px
     *
     * Synced 1:1 with the assets in `assets/snake/` — see `data/snake.config.js`
     * `elements` for the full slot → asset path map.
     */
    OBSTACLE_TYPES: [
        // ── Trees (only two variants ship with art) ─────────
        { type: 'tree',      slot: 'obstacle.tree.single',         color: 0x065f46, shape: 'diamond', w: 2, h: 2 },
        { type: 'tree',      slot: 'obstacle.tree.pine',           color: 0x0f5132, shape: 'diamond', w: 1, h: 2 },

        // ── Bushes ──────────────────────────────────────────
        { type: 'bush',      slot: 'obstacle.bush.single',         color: 0x047857, shape: 'circle',  w: 2, h: 2 },
        { type: 'bush',      slot: 'obstacle.bush.pink',           color: 0xdb2777, shape: 'circle',  w: 4, h: 2 },
        // Small flower bush — art is 60×60 so a 1×1 footprint keeps the
        // hitbox tight around the visible flower.
        { type: 'bush',      slot: 'obstacle.bush.yellow',         color: 0xeab308, shape: 'circle',  w: 1, h: 1 },

        // ── Rocks ───────────────────────────────────────────
        { type: 'rock',      slot: 'obstacle.rock.single',         color: 0x94a3b8, shape: 'rect',    w: 2, h: 2 },
        { type: 'rock',      slot: 'obstacle.rock.large',          color: 0x64748b, shape: 'rect',    w: 3, h: 2 },

        // ── Street decor ───────────────────────────────────
        // Long pink flowerbed — art is 100×60, so a 2×1 footprint sits
        // tight along its visible bed instead of leaving padded cells.
        { type: 'flowerbed', slot: 'obstacle.flowerbed',           color: 0xec4899, shape: 'rect',    w: 2, h: 1 },
        // Single orange flower — 60×60 art, 1×1 footprint.
        { type: 'flowerbed', slot: 'obstacle.flowerbed.orange',    color: 0xf97316, shape: 'rect',    w: 1, h: 1 },
        // Lamp art is a thin post centred in a 100×200 canvas, so the
        // gameplay footprint stays 1×2 (post only) and the PNG scales
        // down — keeps the hitbox aligned with what the player sees.
        { type: 'lamp',      slot: 'obstacle.lamp',                color: 0x6b7280, shape: 'rect',    w: 1, h: 2 },
        // Traffic light art is also a thin post centred in its canvas —
        // hitbox stays 1×2 so it lines up with the visible pole.
        { type: 'lamp',      slot: 'obstacle.traffic_lamp',        color: 0x991b1b, shape: 'rect',    w: 1, h: 2 },
        { type: 'bench',     slot: 'obstacle.bench',               color: 0x78350f, shape: 'rect',    w: 2, h: 1 },
        { type: 'barrier',   slot: 'obstacle.barrier',             color: 0xf59e0b, shape: 'rect',    w: 3, h: 2 },
        { type: 'sign',      slot: 'obstacle.stop',                color: 0xdc2626, shape: 'rect',    w: 2, h: 2 },
        { type: 'truck',     slot: 'obstacle.icecream_truck',      color: 0xf472b6, shape: 'rect',    w: 2, h: 3 },

        // ── Buildings ───────────────────────────────────────
        // Per-board duplicates are already minimised by the pool-based
        // spawner in SnakeObstacles.generate(): every slot is picked at
        // most once before the spawner ever allows a repeat. `maxPerBoard`
        // is only needed if you want a hard cap during the repeat phase.
        { type: 'building',  slot: 'obstacle.building.large',      color: 0x475569, shape: 'rect',   w: 3, h: 3 },
        { type: 'building',  slot: 'obstacle.building.hospital',   color: 0x3b82f6, shape: 'rect',   w: 4, h: 4 },
        { type: 'building',  slot: 'obstacle.building.pink',       color: 0xec4899, shape: 'rect',   w: 4, h: 4 },
        { type: 'building',  slot: 'obstacle.building.modern',     color: 0x06b6d4, shape: 'rect',   w: 3, h: 2 },
        { type: 'building',  slot: 'obstacle.building.restaurant', color: 0xb45309, shape: 'rect',   w: 3, h: 2 },
        { type: 'building',  slot: 'obstacle.building.vegies',     color: 0x16a34a, shape: 'rect',   w: 2, h: 2 },
    ],
    OBSTACLE_SPAWN_MARGIN: 4,

    // ── Exit / multi-board ──────────────────────────────
    EXIT_WIDTH: 3,
    EXIT_COLOR: 0xfbbf24,
    EXIT_PULSE_SPEED: 0.005,

    // ── Perks / Power-ups ──────────────────────────────
    /** Milliseconds between perk spawns (random in range) */
    PERK_SPAWN_INTERVAL_MS: [5000, 10000],
    /** How long a perk stays on the board before vanishing (ms).
     *  Doubled from the legacy 8000ms — the live value is overridable
     *  via the `perkLifetimeSeconds` setting (Options → Snake) so
     *  individual stages / playtest sessions can tune it without
     *  touching the source. SnakePerks reads the override at
     *  construction and falls back here if no setting is stored. */
    PERK_LIFETIME_MS: 16000,
    /** When remaining lifetime drops below this, perk blinks fast.
     *  Scaled in step with PERK_LIFETIME_MS so the warning still feels
     *  proportionate; overridable via `perkCountdownSeconds`. */
    PERK_BLINK_MS: 4000,
    /** Max perks visible on the board at once */
    PERK_MAX_ON_BOARD: 2,
    /**
     * Visual scale of the rendered perk icon relative to its cell.
     * 1.0 = fits inside the cell (cs - 8). 2.0 = double size, spills into
     * the 4 adjacent cells visually but the pickup still lives in one cell.
     * Bumped from 1.0 → 2.0 so perks read clearly at a glance.
     */
    PERK_RENDER_SCALE: 2.0,

    PERK_TYPES: [
        { id: 'shrink',      label: '-1 Car',  color: 0x22c55e, shape: 'triangle_down', durationMs: 0 },
        { id: 'speed_up',    label: 'Speed+',  color: 0xef4444, shape: 'triangle_up',   durationMs: 0 },
        { id: 'random_turn', label: 'Chaos',   color: 0xf97316, shape: 'diamond',       durationMs: 0 },
        { id: 'ghost',       label: 'Ghost',   color: 0x3b82f6, shape: 'ring',          durationMs: 6000 },
        { id: 'slow',        label: 'Slow Mo', color: 0x06b6d4, shape: 'square',        durationMs: 5000 },
    ],

    // ── Lives / invincibility ──────────────────────────
    /** Lives the player gets at the start of every stage. The pool
     *  resets to this value every time the player crosses a portal
     *  to the next stage; running it down to 0 ends the run. */
    INITIAL_LIVES: 3,
    /** Default seconds the snake stays invincible after losing a
     *  life — gives the player a grace window to read the board
     *  before traffic resumes. Overridable per-run via
     *  `invincibleSeconds` (Options → Snake). */
    INVINCIBLE_AFTER_HIT_SECONDS: 5,
    /** Snake alpha while the post-hit invincibility window is
     *  active. Slightly higher than `GHOST_ALPHA` so the visual
     *  reads as "you're protected, keep moving" rather than the
     *  full-on Ghost-mode state. */
    INVINCIBLE_ALPHA: 0.55,

    // ── Free Roam Mode ─────────────────────────────────
    FREEROAM_GRID_MULTIPLIER: 3,
    FREEROAM_DEFAULT_TARGET: 1000,
    FREEROAM_DEFAULT_TIMER: 60,
    FREEROAM_OBSTACLE_DENSITY: 0.06,
    FREEROAM_FOOD_COUNT: 8,

    // ── Adventure Mode ────────────────────────────────
    ADVENTURE_SCREENS: 4,
    ADVENTURE_POINTS_TO_UNLOCK: 100,
    ADVENTURE_DOOR_SIDES: ['right', 'top', 'right', null],
    ADVENTURE_HOUSE_W: 5,
    ADVENTURE_HOUSE_H: 3,
    ADVENTURE_HOUSE_COLOR: 0xef4444,
    ADVENTURE_HOUSE_OPEN_COLOR: 0x22c55e,
    ADVENTURE_DOOR_COLOR: 0xfbbf24,

    /** Ghost mode: snake + obstacle collision disabled */
    GHOST_ALPHA: 0.35,
    /** Slow Mo: tick rate multiplied by this */
    SLOW_MULTIPLIER: 0.5,
    SLOW_MIN_RATE: 3,
    /** Speed+ permanently adds this many tick-rate steps */
    SPEED_UP_STEPS: 2,
    /**
     * Speed+ also enables a timed "boost trail" right after pickup —
     * confetti streams from the snake head, and (if the toggle below is on)
     * obstacles explode on contact instead of ending the run.
     */
    SPEED_UP_TRAIL_MS: 5000,
};

/**
 * UI reserves the play grid must NOT extend into.
 *
 *   topReserve     — clears the {@link SnakeTopBar} (BAR_H = 110) + small gap.
 *   bottomReserve  — clears the bottom perk legend cards
 *                    (CARD_H 96 + CARDS_BOTTOM_PAD 12 = 108) + small gap.
 *                    The D-pad (~232 px from the bottom edge) is
 *                    *intentionally* NOT cleared here — it's rendered at
 *                    low opacity (`PAD_ALPHA ≈ 0.55`) so it can sit over
 *                    the bottom-left of the play area without obscuring
 *                    play. This trade gives the player ~3 extra rows of
 *                    usable arena.
 *   sideReserve    — breathing room from the left / right screen edges so
 *                    perks rendered at PERK_RENDER_SCALE (≈ 2× cell) don't
 *                    spill off-canvas.
 *
 * Keep these in sync if you change the UI component dimensions.
 */
export const SNAKE_UI = Object.freeze({
    topReserve: 140,     // 110 (top bar) + 30 px gap
    bottomReserve: 118,  // 108 (perk cards) + 10 px gap
    sideReserve: 30,
});

/**
 * Compute the play grid for the given canvas size. The returned rectangle
 * (originX, originY, cols × cellSize, rows × cellSize) is guaranteed to sit
 * inside the box [SNAKE_UI.sideReserve, SNAKE_UI.topReserve] —
 * [width − sideReserve, height − bottomReserve], so the game arena never
 * lives under the top bar, D-pad or perk legend cards.
 *
 * Default invocation against the design canvas (1920×1080):
 *   availW = 1920 − 60   = 1860  →  37 cols × 50 px = 1850
 *   availH = 1080 − 258  =  822  →  16 rows × 50 px = 800
 *   originX = 30 + (1860 − 1850) / 2 = 35
 *   originY = 140 + (822 − 800) / 2 = 151
 */
export function computeGrid(width = 1920, height = 1080) {
    const cs = SNAKE_CONFIG.TARGET_CELL_SIZE;
    const availW = Math.max(cs, width  - SNAKE_UI.sideReserve   * 2);
    const availH = Math.max(cs, height - SNAKE_UI.topReserve - SNAKE_UI.bottomReserve);
    const cols = Math.max(8, Math.floor(availW / cs));
    const rows = Math.max(6, Math.floor(availH / cs));
    const originX = Math.round(SNAKE_UI.sideReserve + (availW - cols * cs) / 2);
    const originY = Math.round(SNAKE_UI.topReserve  + (availH - rows * cs) / 2);
    return { cols, rows, cellSize: cs, originX, originY };
}

/**
 * Compute the Free Roam world grid — same play-area rectangle as the base
 * grid, but cols and rows are multiplied by {@link FREEROAM_GRID_MULTIPLIER}
 * so the world is larger than what fits on screen. The camera scrolls the
 * snake inside a viewport equal to the base (`cols × rows`) sized grid.
 */
export function computeLargeGrid(width = 1920, height = 1080) {
    const base = computeGrid(width, height);
    const mult = SNAKE_CONFIG.FREEROAM_GRID_MULTIPLIER;
    return {
        cols: base.cols * mult,
        rows: base.rows * mult,
        cellSize: base.cellSize,
        originX: 0,
        originY: 0,
        viewCols: base.cols,
        viewRows: base.rows,
    };
}

/**
 * Legacy fixed snapshot of the design-canvas grid. Kept exported so any
 * outside reference still resolves; in-code, prefer {@link computeGrid}.
 */
export const SNAKE_GRID = Object.freeze(computeGrid(1920, 1080));

/** Legacy fixed snapshot of the design-canvas large grid. */
export const SNAKE_LARGE_GRID = Object.freeze(computeLargeGrid(1920, 1080));
